import React, { useEffect, useState } from "react";
import { taskApi } from "../../backend/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import {
  TaskDialog,
  TaskDialogActions,
  TaskField,
  TaskMessage,
  TaskTextArea,
  TaskTextInput,
} from "./taskFormUi";

const utilityLabels = {
  electricity: "Electricity",
  gas: "Gas",
  water: "Water",
  wifi: "WiFi / Internet",
};

const emptyUtility = { available: false, onedrive_link: "", notes: "" };

const UtilitiesForm = ({ projectId, onClose, onSave }) => {
  const [utilities, setUtilities] = useState({
    electricity: { ...emptyUtility },
    gas: { ...emptyUtility },
    water: { ...emptyUtility },
    wifi: { ...emptyUtility },
  });
  const [selectedUtility, setSelectedUtility] = useState("electricity");
  const [existingRecord, setExistingRecord] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    const fetchUtilities = async () => {
      if (!projectId) return;

      const { data } = await taskApi.get(projectId);

      if (data) {
        setExistingRecord(data);

        const parsed =
          typeof data.utility_details === "string"
            ? JSON.parse(data.utility_details || "{}")
            : data.utility_details || {};

        setUtilities({
          electricity: parsed.electricity || { ...emptyUtility },
          gas: parsed.gas || { ...emptyUtility },
          water: parsed.water || { ...emptyUtility },
          wifi: parsed.wifi || { ...emptyUtility },
        });
      }
    };

    fetchUtilities();
  }, [projectId]);

  const handleChange = (utilityType, field, value) => {
    setUtilities((previous) => ({
      ...previous,
      [utilityType]: { ...previous[utilityType], [field]: value },
    }));
  };

  const handleSave = async () => {
    if (!projectId) return;
    setErrorMessage("");
    setSuccessMessage("");

    const result = existingRecord?.id
      ? await taskApi.update(existingRecord.id, { utility_details: utilities })
      : await taskApi.update(projectId, { utility_details: utilities });

    if (result.error) {
      setErrorMessage("Failed to save utilities details");
      return;
    }

    setSuccessMessage("Utilities details saved successfully.");
    onSave?.();
    onClose?.();
  };

  const utility = utilities[selectedUtility];

  return (
    <TaskDialog
      title="Utilities Details"
      description="Track utility availability, documents, and notes."
      onClose={onClose}
      className="max-w-xl"
    >
      <div className="space-y-5">
        <TaskMessage>{errorMessage}</TaskMessage>
        <TaskMessage tone="success">{successMessage}</TaskMessage>

        <TaskField id="utility-type" label="Utility Type">
          <Select value={selectedUtility} onValueChange={setSelectedUtility}>
            <SelectTrigger id="utility-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(utilityLabels).map(([value, label]) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </TaskField>

        <div className="rounded-md border bg-muted/20 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">
                {utilityLabels[selectedUtility]} Available
              </p>
              <p className="text-xs text-muted-foreground">
                Toggle on when this service is available or arranged.
              </p>
            </div>
            <Switch
              checked={!!utility.available}
              onCheckedChange={(checked) =>
                handleChange(selectedUtility, "available", checked)
              }
            />
          </div>
        </div>

        <TaskTextInput
          id="utility-link"
          label="OneDrive Document Link"
          type="url"
          value={utility.onedrive_link}
          onChange={(event) =>
            handleChange(selectedUtility, "onedrive_link", event.target.value)
          }
          placeholder="https://..."
        />

        <TaskTextArea
          id="utility-notes"
          label="Notes"
          value={utility.notes}
          onChange={(event) =>
            handleChange(selectedUtility, "notes", event.target.value)
          }
          rows={4}
        />

        <TaskDialogActions
          onCancel={onClose}
          onSubmit={handleSave}
          submitLabel="Save Changes"
        />
      </div>
    </TaskDialog>
  );
};

export default UtilitiesForm;
